/* eslint-disable react-hooks/rules-of-hooks */
import { Box, Grid } from '@apia/theme';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { makeResponsiveComponent } from '../responsive';
import { useStore } from './store/useStore';
import { uniqueId } from '../ListBox/common';
import { makeStyledComponent } from '@apia/theme';
import { KeyHandler } from './KeyHandler';
import { styles } from './styles';
import { getBreakpoints, getRowIcons, useStyles } from './util';
import { IconWrapper } from './IconWrapper';
import { DefaultIconRenderer } from './renderers/DefaultIconRenderer';
import { TIconsList, TIcon, TIconRenderer } from './types';
import { IconRendererContext } from './context';
import { useLatest, usePrevious } from '@apia/util';
import { shallowEqual } from '@apia/store';

export type { TIcon, TIconRenderer, TIconsList };

const IconsListNonForwarded = <P extends Record<string, unknown>>(
  props: TIconsList<P>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const propsRef = useLatest(props);
  let render: () => void = () => {};
  const previousProps = usePrevious(props);
  if (!shallowEqual(props, previousProps.current)) {
    setTimeout(() => render(), 0);
  }

  const ResponsiveContainer = useMemo(() => {
    const iconWidth = propsRef.current.iconWidth ?? 100;
    const minGap = propsRef.current.minHorizontalGap ?? 30;
    const blockWidth = iconWidth + minGap;
    const styledComponent = (styledComponentProps: unknown) => {
      const { breakPoint, containerWidth } = styledComponentProps as {
        breakPoint: number;
        containerWidth: number;
      };
      const [, doRender] = useState(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      render = useCallback(() => doRender((current) => !current), []);
      const storeId = useMemo(() => uniqueId('IconsList'), []);
      const [actions, useProps, , , Context] = useStore({ storeId });
      const rows = Math.ceil(
        propsRef.current.icons.length / Math.max(breakPoint, 1),
      );
      const sx = useStyles({
        breakPoint,
        containerWidth,
        iconWidth: propsRef.current.iconWidth ?? 100,
        verticalGap: propsRef.current.verticalGap,
        removeHorizontalPadding: propsRef.current.removeHorizontalPadding,
      });

      useEffect(() => {
        actions.recalculateIndexByResize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [breakPoint]);
      const IconRenderer = propsRef.current.iconRenderer ?? DefaultIconRenderer;
      const gridRef = useProps('grid', {
        columns: Math.max(breakPoint, 1),
        iconsCount: propsRef.current.icons.length,
        rows,
      });

      return (
        <IconRendererContext.Provider value={IconRenderer}>
          <Context>
            <KeyHandler
              icons={propsRef.current.icons}
              onIconClick={propsRef.current.onIconClick}
              onIconDoubleClick={propsRef.current.onIconDoubleClick}
              onIconGetDetails={propsRef.current.onIconGetDetails}
              onIconKeyDown={propsRef.current.onIconKeyDown}
              storeId={storeId}
            >
              <Box ref={gridRef}>
                {Array(rows)
                  .fill(1)
                  .map((_, i) => {
                    const icons = getRowIcons({
                      breakPoint,
                      i,
                      icons: propsRef.current.icons,
                    });
                    return (
                      <Grid
                        aria-rowindex={i + 1}
                        role="row"
                        key={i}
                        ref={ref}
                        sx={sx}
                      >
                        {icons.map((current, j) => (
                          <IconWrapper
                            key={uniqueId(String(j + i))}
                            colIndex={j + 1}
                            icon={current}
                            iconWidth={propsRef.current.iconWidth ?? 100}
                            rowIndex={i + 1}
                          />
                        ))}
                      </Grid>
                    );
                  })}
              </Box>
            </KeyHandler>
          </Context>
        </IconRendererContext.Provider>
      );
    };

    const Component = makeResponsiveComponent({
      breakPoints: getBreakpoints(blockWidth),
      Component: makeStyledComponent(
        'IconsList',
        'layout.common.components.iconsList',
        styles,
        styledComponent,
      ),
      debounce: 50,
    });

    return Component;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (previousProps.current?.iconWidth !== props.iconWidth) {
    const iconWidth = props.iconWidth ?? 100;
    const minGap = props.minHorizontalGap ?? 30;

    const blockWidth = iconWidth + minGap;
    setTimeout(
      () =>
        ResponsiveContainer.setBreakpoints.current(getBreakpoints(blockWidth)),
      0,
    );
  }

  return <ResponsiveContainer />;
};

IconsListNonForwarded.displayName = 'IconsList';

export const IconsList = forwardRef(
  IconsListNonForwarded,
) as typeof IconsListNonForwarded;

export { DefaultIconRenderer };
